// Calls the SIVAN API through the same-origin `/api` proxy (vite.config.ts). Responses are parsed
// with the shared contracts, so the UI never works with unexpected shapes.
/** Any shared contract schema (zod): only `parse` is used. */
export interface Schema<T> {
  parse(input: unknown): T;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';

  constructor(
    readonly status: number,
    readonly body: Record<string, unknown>,
  ) {
    super(`API ${String(status)}`);
  }

  get requestId(): string | null {
    return typeof this.body['requestId'] === 'string' ? this.body['requestId'] : null;
  }
}

/** The request never got an answer (network down, server unreachable): its outcome is unknown. */
export class NetworkError extends Error {
  override readonly name = 'NetworkError';
}

/**
 * The signed-in session's CSRF token (AUTH_MODE=oidc), kept in memory only; sent with every
 * state-changing request. Null in the development identity mode.
 */
let csrfToken: string | null = null;
export const setCsrfToken = (token: string | null): void => {
  csrfToken = token;
};
const csrfHeaders = (): Record<string, string> =>
  csrfToken === null ? {} : { 'x-csrf-token': csrfToken };

/** Listeners told when the server no longer recognizes the session (401). */
const signedOutListeners = new Set<() => void>();
export function onSignedOut(listener: () => void): () => void {
  signedOutListeners.add(listener);
  return () => {
    signedOutListeners.delete(listener);
  };
}
const reportStatus = (status: number): void => {
  if (status === 401 && csrfToken !== null) for (const listener of signedOutListeners) listener();
};

interface CallOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly body?: unknown;
}

async function call<T>(path: string, schema: Schema<T>, options: CallOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: options.method ?? 'GET',
      headers:
        options.body === undefined
          ? { accept: 'application/json' }
          : { accept: 'application/json', 'content-type': 'application/json', ...csrfHeaders() },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new NetworkError('no response');
  }
  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  reportStatus(response.status);
  if (!response.ok) throw new ApiError(response.status, body);
  return schema.parse(response.status === 204 ? undefined : body);
}

export const apiGet = <T>(path: string, schema: Schema<T>): Promise<T> => call(path, schema);

export const apiPost = <T>(path: string, body: unknown, schema: Schema<T>): Promise<T> =>
  call(path, schema, { method: 'POST', body });

export const apiPut = <T>(path: string, body: unknown, schema: Schema<T>): Promise<T> =>
  call(path, schema, { method: 'PUT', body });

/** A DELETE with an empty JSON body (write protection requires a JSON content type). */
export const apiDelete = async (path: string): Promise<void> => {
  await call(path, { parse: () => undefined }, { method: 'DELETE', body: {} });
};

/**
 * Uploads a binary body (a photo) with progress, which fetch cannot report. Resolves with the
 * parsed response; rejects with ApiError or NetworkError like the other calls.
 */
export function apiUpload<T>(
  path: string,
  body: Blob,
  schema: Schema<T>,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', `/api${path}`);
    request.setRequestHeader('content-type', body.type);
    request.setRequestHeader('accept', 'application/json');
    for (const [name, value] of Object.entries(csrfHeaders()))
      request.setRequestHeader(name, value);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(request.responseText) as Record<string, unknown>;
      } catch {
        // Not JSON: keep the empty body.
      }
      reportStatus(request.status);
      if (request.status >= 200 && request.status < 300) {
        try {
          resolve(schema.parse(parsed));
        } catch (error) {
          reject(error instanceof Error ? error : new Error('invalid response'));
        }
      } else reject(new ApiError(request.status, parsed));
    };
    request.onerror = () => {
      reject(new NetworkError('no response'));
    };
    request.onabort = () => {
      reject(new NetworkError('aborted'));
    };
    signal?.addEventListener('abort', () => {
      request.abort();
    });
    request.send(body);
  });
}
